"use client";
import { useEffect } from "react";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";

const { Text } = Typography;

const employmentStatusOptions = [
  { value: "salaried_private", label: "Salaried -- Private sector" },
  { value: "salaried_public", label: "Salaried -- Public sector" },
  { value: "self_employed", label: "Self-employed" },
  { value: "business_owner", label: "Business owner" },
  { value: "retired", label: "Retired" },
];

// The currencies endpoint only returns { code, name }, so map the common
// ISO 4217 codes to a display symbol and fall back to the code itself.
const CURRENCY_SYMBOLS = {
  NGN: "₦", USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥",
  GHS: "₵", KES: "KSh", ZAR: "R", XOF: "CFA", XAF: "FCFA", INR: "₹",
  AUD: "A$", CAD: "C$", CHF: "CHF", AED: "د.إ", SAR: "﷼", EGP: "E£",
  MAD: "DH", TZS: "TSh", UGX: "USh", RWF: "FRw", ZMW: "ZK", GMD: "D",
};

// Used only until the /currencies call resolves (or if it fails).
const FALLBACK_CURRENCIES = [
  { value: "NGN", code: "NGN", name: "Nigerian Naira", label: "NGN -- Nigerian Naira" },
  { value: "USD", code: "USD", name: "US Dollar", label: "USD -- US Dollar" },
  { value: "EUR", code: "EUR", name: "Euro", label: "EUR -- Euro" },
  { value: "GBP", code: "GBP", name: "British Pound", label: "GBP -- British Pound" },
];

const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || code || "";

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

const CustomerLookup = (props) => {
  const {
    onFinish,
    form,
    initialValues,
    searchData,
    onSearch,
    searchParam,
    currencyList,
  } = props;

  const isSearchDisabled =
    !searchParam || searchParam?.length < 5 ? true : false;

  const currencyOptions =
    currencyList && currencyList.length ? currencyList : FALLBACK_CURRENCIES;
  const defaultCurrency =
    currencyOptions.find((c) => c.value === "NGN")?.value ||
    currencyOptions[0]?.value;

  const selectedCurrency = Form.useWatch("currency", form);
  const currencySymbol = getCurrencySymbol(selectedCurrency || defaultCurrency);

  useEffect(() => {
    if (!form.getFieldValue("currency") && defaultCurrency) {
      form.setFieldsValue({ currency: defaultCurrency });
    }
  }, [form, defaultCurrency]);

  // Prefill the form from the resolved customer record.
  useEffect(() => {
    if (!searchData) return;
    form.setFieldsValue({
      nationalId:
        searchData.entityExternalId || form.getFieldValue("nationalId"),
      primarySavingsAccount: searchData.entityAccountNo || "",
      loanDisbursementAccount: searchData.entityAccountNo || undefined,
    });
  }, [searchData, form]);

  const accountOptions = searchData?.entityAccountNo
    ? [
        {
          value: searchData.entityAccountNo,
          label: searchData.entityName
            ? `${searchData.entityAccountNo} -- ${searchData.entityName}`
            : searchData.entityAccountNo,
        },
      ]
    : [];

  const currencyAddon = (
    <span style={{ fontWeight: 500, color: "#344054", minWidth: 16, display: "inline-block", textAlign: "center" }}>
      {currencySymbol}
    </span>
  );

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "";
    return (
      (parts[0][0] || "").toUpperCase() +
      (parts[parts.length - 1][0] || "").toUpperCase()
    );
  };

  return (
    <div>
      <Form
        className="custom-form"
        layout="vertical"
        onFinish={onFinish}
        form={form}
        initialValues={initialValues}
        style={{ width: "100%" }}
      >
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Customer ID
                  <Tooltip title="Enter the Customer Identification number">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              required
            >
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="customerId"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Customer ID is required",
                    },
                  ]}
                >
                  <Input
                    className="bond-input"
                    style={{ height: 44 }}
                    placeholder="Enter Customer ID"
                    onPressEnter={onSearch}
                    allowClear
                  />
                </Form.Item>
                <Button
                  onClick={onSearch}
                  className="of-primaryBtnColor"
                  style={{ height: 44 }}
                  disabled={isSearchDisabled}
                >
                  <SearchOutlined />
                </Button>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  National ID
                  <Tooltip title="Enter the National ID number">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="nationalId"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="National ID number"
                readOnly={!!searchData?.entityExternalId}
              />
            </Form.Item>
          </Col>
        </Row>

        {searchData && (
          <div
            style={{
              background: "#f8faf9",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#15CCAE",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {getInitials(searchData?.entityName)}
              </div>
              <div>
                <Text strong style={{ fontSize: 15 }}>
                  {searchData?.entityName}
                </Text>
                <div
                  style={{
                    color: "#667085",
                    fontSize: 13,
                    marginTop: 2,
                  }}
                >
                  {[
                    searchData?.entityAccountNo &&
                      `Acct: ${searchData.entityAccountNo}`,
                    searchData?.entityType,
                    searchData?.entityMobileNo,
                    searchData?.entityBranch,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                  {searchData?.entityStatus && (
                    <span
                      style={{
                        background: "#ecfdf5",
                        color: "#059669",
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "2px 10px",
                        borderRadius: 12,
                        border: "1px solid #a7f3d0",
                      }}
                    >
                      {searchData.entityStatus}
                    </span>
                  )}
                  {searchData?.entityExternalId && (
                    <span
                      style={{
                        background: "#ecfdf5",
                        color: "#059669",
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "2px 10px",
                        borderRadius: 12,
                        border: "1px solid #a7f3d0",
                      }}
                    >
                      ID: {searchData.entityExternalId}
                    </span>
                  )}
                  {searchData?.entityStaff && (
                    <span
                      style={{
                        background: "#ecfdf5",
                        color: "#059669",
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "2px 10px",
                        borderRadius: 12,
                        border: "1px solid #a7f3d0",
                      }}
                    >
                      RM: {searchData.entityStaff}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#667085",
                  letterSpacing: "0.05em",
                  marginBottom: 4,
                }}
              >
                CREDIT SCORE
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: "#344054",
                  lineHeight: 1,
                }}
              >
                {searchData?.creditScore || "742"}
              </div>
              <span
                style={{
                  background: "#ecfdf5",
                  color: "#059669",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "2px 10px",
                  borderRadius: 12,
                  border: "1px solid #a7f3d0",
                  marginTop: 4,
                  display: "inline-block",
                }}
              >
                GOOD
              </span>
            </div>
          </div>
        )}

        <div style={sectionDividerStyle}>
          <div style={sectionHeaderStyle}>EMPLOYMENT & INCOME DETAILS</div>
        </div>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Currency
                  <Tooltip title="Currency for all monetary values on this application">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="currency"
              rules={[
                {
                  required: true,
                  message: "Currency is required",
                },
              ]}
            >
              <Select
                placeholder="Select currency"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={currencyOptions}
                optionFilterProp="label"
                showSearch
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Employment Status
                  <Tooltip title="Select the customer's employment status">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="employmentStatus"
              rules={[
                {
                  required: true,
                  message: "Employment status is required",
                },
              ]}
            >
              <Select
                placeholder="Select employment status"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={employmentStatusOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Employer / Business Name
                  <Tooltip title="Enter the employer or business name">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="employerName"
              rules={[
                {
                  required: true,
                  message: "Employer or business name is required",
                },
              ]}
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Enter employer or business name"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Monthly Net Income
                  <Tooltip title="Enter monthly net income after deductions">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="monthlyNetIncome"
              rules={[
                {
                  required: true,
                  message: "Monthly net income is required",
                },
              ]}
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                addonBefore={currencyAddon}
                placeholder="0.00"
                type="number"
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Other Monthly Income
                  <Tooltip title="Enter any additional monthly income">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="otherMonthlyIncome"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                addonBefore={currencyAddon}
                placeholder="0.00"
                type="number"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Monthly Obligations
                  <Tooltip title="Enter existing monthly obligations (EMIs, rent, etc.)">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="monthlyObligations"
              help="Existing EMIs, rent, etc."
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                addonBefore={currencyAddon}
                placeholder="0.00"
                type="number"
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={sectionDividerStyle}>
          <div style={sectionHeaderStyle}>EXISTING ACCOUNTS</div>
        </div>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Primary Savings Account
                  <Tooltip title="Customer's primary savings account">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="primarySavingsAccount"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Auto-populated from customer profile"
                readOnly
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Loan Disbursement Account
                  <Tooltip title="Select account for loan disbursement">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="loanDisbursementAccount"
              rules={[
                {
                  required: true,
                  message: "Loan disbursement account is required",
                },
              ]}
            >
              <Select
                placeholder="Select disbursement account"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={accountOptions}
                notFoundContent="Look up a customer first"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CustomerLookup;
